import { useEffect, useState } from "react";
import axios from "axios";
import { FileDown } from "lucide-react";
import { downloadPdf } from "@/lib/download";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const MONTH_NAMES = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const COLORS = ["#2563eb", "#0f9f6e", "#f59e0b", "#d0457f", "#7c3aed", "#0891b2", "#dc2626", "#65a30d"];
const thisYear = new Date().getFullYear();

function Comparison({ teams }) {
  const sorted = [...teams].sort((a, b) => b.earnings - a.earnings);
  const max = Math.max(1, ...sorted.map((t) => t.earnings));
  return (
    <section className="panel" data-testid="team-comparison">
      <div className="panel-head"><div><h3>Ekip karşılaştırması</h3><p>Yıllık ekip kazancına göre sıralama</p></div></div>
      <div className="compare-row head"><span>#</span><span>EKİP</span><span /><span>EKİP KAZANCI</span><span>KOMİSYON</span><span>SÖZLEŞME</span></div>
      {sorted.map((t, i) => (
        <div className="compare-row" key={t.id} data-testid={`compare-row-${i}`}>
          <span className={i === 0 && t.earnings > 0 ? "rank first" : "rank"}>{i + 1}</span><b>{t.name}</b>
          <div className="compare-bar"><i style={{ width: `${(t.earnings / max) * 100}%`, background: COLORS[teams.indexOf(t) % COLORS.length] }} /></div>
          <strong className="income-text" data-testid={`compare-earnings-${i}`}>{money(t.earnings)}</strong><span>{money(t.commission)}</span><span>{t.count}</span>
        </div>
      ))}
    </section>
  );
}

export default function TeamReport() {
  const [year, setYear] = useState(thisYear), [data, setData] = useState(null), [month, setMonth] = useState(new Date().getMonth() + 1), [member, setMember] = useState("");
  const mm = String(month).padStart(2, "0");
  const memberId = member || data?.teams[0]?.id;
  const memberSlug = (data?.teams.find((t) => t.id === memberId)?.name || "eleman").toLocaleLowerCase("tr-TR").replace(/\s+/g, "-");
  const salesPdf = () => downloadPdf(`/teams/${memberId}/sales.pdf`, `satis-kaydi-${memberSlug}-${year}-${mm}.pdf`, { year, month });
  useEffect(() => { axios.get(`${API}/teams/report`, { params: { year } }).then((r) => setData(r.data)).catch(() => {}); }, [year]);
  const chart = data?.months.map((m, i) => ({ ...m, label: MONTHS[i] })) || [];
  return (
    <>
      <div className="page-title"><div><span className="eyebrow">ANALİZ</span><h1 data-testid="page-title">Ekip Raporu</h1><p>Her ekibin aylık kazancı (ekip payı) ve ekiplerin karşılaştırması.</p></div></div>
      <div className="report-toolbar">
        <span className="eyebrow" style={{ margin: 0 }}>YIL</span>
        <select data-testid="team-report-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="eyebrow" style={{ margin: "0 0 0 14px" }}>AY</span>
        <select data-testid="team-report-month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <button className="primary-btn" onClick={() => downloadPdf("/teams/report/monthly.pdf", `ekip-raporu-${year}-${mm}.pdf`, { year, month })} data-testid="team-report-pdf-button"><FileDown size={15} />Ekip raporu PDF</button>
      </div>
      {data?.teams.length > 0 && (
        <div className="report-toolbar sales-toolbar" data-testid="sales-record-toolbar">
          <span className="eyebrow" style={{ margin: 0 }}>SATIŞ KAYDI</span>
          <select data-testid="sales-record-member-select" value={memberId} onChange={(e) => setMember(e.target.value)}>
            {data.teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="outline-btn" onClick={salesPdf} data-testid="sales-record-pdf-button"><FileDown size={15} />Aylık · {MONTH_NAMES[month - 1]}</button>
          <button className="outline-btn" onClick={() => downloadPdf(`/teams/${memberId}/sales-year.pdf`, `yillik-satis-kaydi-${memberSlug}-${year}.pdf`, { year })} data-testid="sales-record-year-pdf-button"><FileDown size={15} />Yıllık · {year}</button>
          <span className="toolbar-divider" />
          <span className="eyebrow" style={{ margin: 0 }}>TÜM EKİP</span>
          <button className="outline-btn" onClick={() => downloadPdf("/teams/sales.pdf", `satis-kaydi-tum-ekip-${year}-${mm}.pdf`, { year, month })} data-testid="sales-record-all-pdf-button"><FileDown size={15} />Aylık · {MONTH_NAMES[month - 1]}</button>
          <button className="outline-btn" onClick={() => downloadPdf("/teams/sales-year.pdf", `yillik-satis-kaydi-tum-ekip-${year}.pdf`, { year })} data-testid="sales-record-all-year-pdf-button"><FileDown size={15} />Yıllık · {year}</button>
        </div>
      )}
      {data && (
        <>
          <section className="panel" style={{ marginBottom: 20 }} data-testid="team-monthly-chart">
            <div className="panel-head"><div><h3>Aylık ekip kazancı</h3><p>{year} yılı, tapu/teslim tarihine göre</p></div></div>
            <div className="team-report-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} barGap={2}>
                  <CartesianGrid vertical={false} stroke="#e8eef5" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}B` : v)} />
                  <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#f4f7fb" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
                  {data.teams.map((t, i) => <Bar key={t.id} dataKey={t.id} name={t.name} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
          <Comparison teams={data.teams} />
        </>
      )}
    </>
  );
}
